import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import DomHelper from '../../Core/helper/DomHelper.js';
import MessageDialog from '../../Core/widget/MessageDialog.js';
import '../widget/GanttTaskEditor.js';
import '../widget/SchedulerTaskEditor.js';
import ObjectHelper from '../../Core/helper/ObjectHelper.js';
import { ProjectType } from '../../Engine/scheduling/Types.js';
import TaskEditBase from './TaskEditBase.js';

/**
 * @module SchedulerPro/feature/TaskEdit
 */

/**
 * Feature that displays a {@link SchedulerPro.widget.SchedulerTaskEditor Task editor}, allowing users to edit task data.
 * The Task editor is a popup containing tabs with fields for editing task data.
 *
 * This demo shows the task edit feature, double-click child task bar to start editing:
 *
 * {@inlineexample SchedulerPro/feature/TaskEdit.js}
 *
 * ## Customizing tabs and their widgets
 *
 * To customize tabs you can:
 *
 * * Reconfigure built-in tabs by providing override configs in the {@link #config-items} config.
 * * Remove existing tabs or add your own in the {@link #config-items} config.
 * * Advanced: Reconfigure the whole editor widget using {@link #config-editorConfig} or replace the whole editor using
 *   {@link #config-editorClass}.
 *
 * To add extra items to a tab you need to specify {@link Core.widget.Container#config-items} for the tab container.
 *
 * {@note}Note that object notation is used to specify all built-in items in the tabs in the editor, thus object
 * notation has to be used to manipulate them - array notation is not supported.{/@note}
 *
 * This demo shows adding of custom widgets to the task editor, double-click child task bar to start editing:
 *
 * {@inlineexample SchedulerPro/feature/TaskEditExtraItems.js}
 *
 * {@region Expand to see Default tabs and fields}
 *
 * The {@link SchedulerPro.widget.SchedulerTaskEditor Task editor} contains tabs by default. Each tab contains built-in
 * widgets: text fields, grids, etc.
 *
 * | Tab ref           | Type                                                        | Text         | Weight | Description                                                            |
 * |-------------------|-------------------------------------------------------------|--------------|--------|------------------------------------------------------------------------|
 * | `generalTab`      | {@link SchedulerPro.widget.taskeditor.SchedulerGeneralTab}  | General      | 100    | Basic fields: name, resources, start/end dates, duration, percent done |
 * | `predecessorsTab` | {@link SchedulerPro.widget.taskeditor.PredecessorsTab}      | Predecessors | 200    | Shows a grid with incoming dependencies                                |
 * | `successorsTab`   | {@link SchedulerPro.widget.taskeditor.SuccessorsTab}        | Successors   | 300    | Shows a grid with outgoing dependencies                                |
 * | `advancedTab`     | {@link SchedulerPro.widget.taskeditor.SchedulerAdvancedTab} | Advanced     | 500    | Shows advanced configuration: constraints and manual scheduling mode   |
 * | `notesTab`        | {@link SchedulerPro.widget.taskeditor.NotesTab}             | Notes        | 600    | Shows a text area to add notes to the selected task                    |
 *
 * ### General tab
 *
 * General tab contains fields for basic configurations
 *
 * | Field ref          | Type                                     | Text       | Weight | Description                                           |
 * |--------------------|------------------------------------------|------------|--------|-------------------------------------------------------|
 * | `nameField`        | {@link Core.widget.TextField}            | Name       | 100    | Task name                                             |
 * | `resourcesField`   | {@link Core.widget.Combo}                | Resources  | 200    | Shows a list of available resources for this task     |
 * | `startDateField`   | {@link Core.widget.DateTimeField}        | Start      | 300    | Shows when the task begins                            |
 * | `endDateField`     | {@link Core.widget.DateTimeField}        | Finish     | 400    | Shows when the task ends                              |
 * | `durationField`    | {@link Core.widget.DurationField}        | Duration   | 500    | Shows how long the task is                            |
 * | `percentDoneField` | {@link Core.widget.NumberField}          | % Complete | 600    | Shows what part of task is done already in percentage |
 * | `colorField` ¹     | {@link Scheduler.widget.EventColorField} | Color ¹    | 700    | Choose background color for the task bar              |
 *
 * **¹** Set the {@link SchedulerPro.view.SchedulerProBase#config-showTaskColorPickers} config to `true` to enable this field
 *
 * ### Predecessors tab
 *
 * Predecessors tab contains a grid with incoming dependencies and controls to remove/add dependencies
 *
 * | Widget ref | Type                        | Weight | Description                                                                        |
 * |------------|-----------------------------|--------|------------------------------------------------------------------------------------|
 * | `grid`     | {@link Grid.view.Grid}      | 100    | Shows predecessors task name, dependency type and lag                              |
 * | `toolbar`  | {@link Core.widget.Toolbar} | 200    | Shows control buttons                                                              |
 * | \>`add`    | {@link Core.widget.Button}  | 210    | Adds a new predecessor. Then select a task from the list in the name column editor |
 * | \>`remove` | {@link Core.widget.Button}  | 220    | Removes selected incoming dependency                                               |
 *
 * <dl class="foot-note">
 *     <dt>></dt><dd>first level of submenu</dd>
 * </dl>
 *
 * ### Successors tab
 *
 * Successors tab contains a grid with outgoing dependencies and controls to remove/add dependencies
 *
 * | Widget ref | Type                        | Weight | Description                                                                      |
 * |------------|-----------------------------|--------|----------------------------------------------------------------------------------|
 * | `grid`     | {@link Grid.view.Grid}      | 100    | Shows successors task name, dependency type and lag                              |
 * | `toolbar`  | {@link Core.widget.Toolbar} | 200    | Shows control buttons                                                            |
 * | \>`add`    | {@link Core.widget.Button}  | 210    | Adds a new successor. Then select a task from the list in the name column editor |
 * | \>`remove` | {@link Core.widget.Button}  | 220    | Removes selected outgoing dependency                                             |
 *
 * <dl class="foot-note">
 *     <dt>></dt><dd>first level of submenu</dd>
 * </dl>
 *
 * ### Advanced tab
 *
 * Advanced tab contains additional task scheduling options
 *
 * | Field ref                     | Type                                             | Weight | Description                                                                    |
 * |-------------------------------|--------------------------------------------------|--------|--------------------------------------------------------------------------------|
 * | `calendarField`               | {@link SchedulerPro.widget.CalendarField}        | 100    | List of available calendars for this task. Shown when calendars are downloaded |
 * | `constraintTypeField`         | {@link SchedulerPro.widget.ConstraintTypePicker} | 200    | Shows a list of available constraints for this task                            |
 * | `constraintDateField`         | {@link Core.widget.DateField}                    | 300    | Shows a date for the selected constraint type                                  |
 * | `manuallyScheduledField`      | {@link Core.widget.Checkbox}                     | 400    | If checked, the task is not considered in scheduling                           |
 * | `inactiveField`               | {@link Core.widget.Checkbox}                     | 500    | Allows inactivating the task so it won't take part in the scheduling process.  |
 * | `ignoreResourceCalendarField` | {@link Core.widget.Checkbox}                     | 600    | If checked the event ignores the assigned resource calendars when scheduling   |
 *
 * ### Notes tab
 *
 * Notes tab contains a text area to show notes
 *
 * | Field ref   | Type                              | Weight | Description                                     |
 * |-------------|-----------------------------------|--------|-------------------------------------------------|
 * | `noteField` | {@link Core.widget.TextAreaField} | 100    | Shows a text area to add text notes to the task |
 *
 * The built-in buttons are:
 *
 * | Widget ref             | Type                        | Weight | Description                                                    |
 * |------------------------|-----------------------------|--------|----------------------------------------------------------------|
 * | `saveButton`           | {@link Core.widget.Button}  | 100    | Save event button on the bbar                                  |
 * | `deleteButton`         | {@link Core.widget.Button}  | 200    | Delete event button on the bbar                                |
 * | `cancelButton`         | {@link Core.widget.Button}  | 300    | Cancel event editing button on the bbar                        |
 *
 * {@endregion}
 *
 * ## Removing a built-in item or toolbar button
 *
 * To remove a built-in toolbar button, tab or field, specify its `ref` as `false` in the `items` config:
 *
 * ```javascript
 * const scheduler = new SchedulerPro({
 *     features : {
 *         taskEdit : {
 *             items : {
 *                 generalTab      : {
 *                     items : {
 *                         // Remove "Duration" and "% Complete" fields in the "General" tab
 *                         durationField    : false,
 *                         percentDoneField : false
 *                     }
 *                 },
 *                 // Remove all tabs except the "General" tab
 *                 notesTab        : false,
 *                 predecessorsTab : false,
 *                 successorsTab   : false,
 *                 advancedTab     : false
 *             },
 *             editorConfig : {
 *                 bbar : {
 *                     // Remove delete button
 *                     items : {
 *                         deleteButton : false
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Customizing a built-in item
 *
 * To customize a built-in tab or field, use its `ref` as the key in the `items` config and specify the configs you want
 * to change (they will be merged with the tabs or fields default configs correspondingly):
 *
 * ```javascript
 * const scheduler = new SchedulerPro({
 *     features : {
 *         taskEdit : {
 *             items : {
 *                 generalTab      : {
 *                     // Rename "General" tab
 *                     title : 'Main',
 *                     items : {
 *                         // Rename "% Complete" field
 *                         percentDoneField : {
 *                             label : 'Status'
 *                         }
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Adding a custom item
 *
 * To add a custom tab or field, add an entry to the `items` config. When you add a field,
 * the `name` property links the input field to a field in the loaded task record:
 *
 * ```javascript
 * const scheduler = new SchedulerPro({
 *     features : {
 *         taskEdit : {
 *             items : {
 *                 generalTab : {
 *                     items : {
 *                         // Add new field to the last position
 *                         newGeneralField : {
 *                             type   : 'textfield',
 *                             weight : 610,
 *                             label  : 'New field in General Tab',
 *                             // Name of the field matches data field name, so value is loaded/saved automatically
 *                             name   : 'custom'
 *                         }
 *                     }
 *                 },
 *                 // Add a custom tab to the first position
 *                 newTab     : {
 *                     // Tab is a FormTab by default
 *                     title  : 'New tab',
 *                     weight : 90,
 *                     items  : {
 *                         newTabField : {
 *                             type   : 'textfield',
 *                             weight : 10,
 *                             label  : 'New field in New Tab',
 *                             // Name of the field matches data field name, so value is loaded/saved automatically.
 *                             // In this case it is equal to the Task "name" field.
 *                             name   : 'name'
 *                         }
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Manipulating TaskEditor items at run time
 *
 * To change input items depending upon the task being edited, use a {@link #event-beforeTaskEditShow}
 * listener to access the {@link SchedulerPro.widget.SchedulerTaskEditor editor} instance.
 *
 * The available widgets are described {@link #expand-to-see-default-tabs-and-fields here}.
 *
 * The {@link SchedulerPro.widget.SchedulerTaskEditor editor} exposes all its descendant widgets in its
 * {@link Core.widget.Container#property-widgetMap}.
 *
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         taskEdit : true
 *     },
 *     listeners : {
 *         // When editing a parent task, the user may not edit the duration.
 *         // When editing a leaf level task she may edit the duration.
 *         beforeTaskEditShow({ taskRecord, editor }) {
 *             if (taskRecord.isParent) {
 *                 editor.widgetMap.duration.disabled = true;
 *             }
 *             else {
 *                 editor.widgetMap.duration.disabled = false;
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * To turn off the Task Editor just simple disable the feature.
 *
 * ```javascript
 * const scheduler = new SchedulerPro({
 *     features : {
 *         taskEdit : false
 *     }
 * })
 * ```
 *
 * By default predecessors and successors in successorsTab and predecessorsTab are displayed using task id and a name.
 *  The id part is configurable, any task field may be used instead (for example wbsCode or sequence number)
 * by Gantt `dependencyIdField` property, to set it globally, or using
 *  taskEdit config {@link SchedulerPro/widget/TaskEditorBase#config-dependencyIdField} to set format only for taskEditor.
 * ```javascript
 * const gantt = new Gantt({
 *    dependencyIdField: 'wbsCode', // for global format
 *
 *    project,
 *    columns : [
 *        { type : 'name', width : 250 }
 *    ],
 *    features : {
 *         taskEdit : {
 *             editorConfig : {
 *                 dependencyIdField : 'wbsCode' // set only for taskEditor
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * For more info on customizing the Task Editor, please see Guides/Customization/Customize task editor
 *
 * ## Editing nested events
 *
 * Note that when editing nested events the resource field, the successors tab and the predecessors tab are hidden
 * automatically.
 *
 * This feature is **disabled** by default in `SchedulerPro` and **disabled** in `ResourceHistogram`.
 * For info on enabling it, see {@link Grid/view/mixin/GridFeatures}.
 *
 * @extends SchedulerPro/feature/TaskEditBase
 * @demo SchedulerPro/taskeditor
 * @classtype taskEdit
 * @feature
 */
export default class TaskEdit extends TaskEditBase {

    //region Events
    /**
     * Fires on the owning Scheduler or Gantt widget instance before a task is displayed in the editor.
     * This may be listened to in order to take over the task editing flow. Returning `false`
     * stops the default editing UI from being shown.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new SchedulerPro({
     *     listeners : {
     *         async beforeTaskEdit() {
     *            await asyncCheckOfRightsOnBackend();
     *         }
     *     }
     * });
     * ```
     * @event beforeTaskEdit
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.feature.TaskEdit} taskEdit The taskEdit feature
     * @param {SchedulerPro.model.EventModel} taskRecord The task about to be shown in the editor.
     * @param {HTMLElement} taskElement The element which represents the task
     * @preventable
     * @async
     */

    /**
     * Fires on the owning Scheduler or Gantt widget when the editor for an event is canceled.
     * @event taskEditCanceled
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.model.EventModel} taskRecord the task about the shown in the editor
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor
     */

    /**
     * Fires on the owning Scheduler or Gantt widget when the editor for an event is available but before it is shown. Allows
     * manipulating fields etc.
     * @event beforeTaskEditShow
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The SchedulerPro instance
     * @param {SchedulerPro.feature.TaskEdit} taskEdit The taskEdit feature
     * @param {SchedulerPro.model.EventModel} taskRecord The task about to be shown in the editor.
     * @param {HTMLElement} eventElement The element which represents the task
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor
     */

    /**
     * Fires on the owning Scheduler or Gantt widget instance before a task is saved, return `false` to prevent it.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new SchedulerPro({
     *     listeners : {
     *         async beforeTaskSave() {
     *            await someAsyncConditionLikeAskingForConfirmation();
     *         }
     *     }
     * });
     * ```
     *
     * @event beforeTaskSave
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.model.EventModel} taskRecord The task about to be saved
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     * @preventable
     * @async
     */

    /**
     * Fires on the owning Scheduler or Gantt widget instance after a task is saved
     * @event afterTaskSave
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.model.EventModel} taskRecord The task about to be saved
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     */

    /**
     * Fires on the owning Scheduler or Gantt widget instance after task editing is finished by applying changes, cancelling them
     * or deleting the task record.
     * @event afterTaskEdit
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.model.EventModel} taskRecord Task record used in the task editor
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     */

    /**
     * Fires on the owning Scheduler or Gantt widget before a task is deleted, return `false` to prevent it.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new SchedulerPro({
     *     listeners : {
     *         async beforeTaskDelete() {
     *            await someAsyncConditionLikeAskingForConfirmation();
     *         }
     *     }
     * });
     * ```
     *
     * @event beforeTaskDelete
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance.
     * @param {SchedulerPro.model.EventModel} taskRecord The record about to be deleted
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     * @preventable
     * @async
     */

    /**
     * Fires on the owning Scheduler or Gantt widget instance before an event record is saved, return `false` to prevent it.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new SchedulerPro({
     *     listeners : {
     *         async beforeEventSave() {
     *            await someAsyncConditionLikeAskingForConfirmation();
     *         }
     *     }
     * });
     * ```
     *
     * @event beforeEventSave
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.model.EventModel} eventRecord The event record about to be saved
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     * @preventable
     * @async
     */

    /**
     * Fires on the owning Scheduler or Gantt widget instance after an event record is saved
     * @event afterEventSave
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance
     * @param {SchedulerPro.model.EventModel} eventRecord The event record about to be saved
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     */

    /**
     * Fires on the owning Scheduler or Gantt widget before an event record is deleted, return `false` to prevent it.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new SchedulerPro({
     *     listeners : {
     *         async beforeEventDelete() {
     *            await someAsyncConditionLikeAskingForConfirmation();
     *         }
     *     }
     * });
     * ```
     * @event beforeEventDelete
     * @on-owner
     * @param {SchedulerPro.view.SchedulerPro} source The Scheduler Pro instance.
     * @param {SchedulerPro.model.EventModel} eventRecord The event record about to be deleted
     * @param {SchedulerPro.widget.TaskEditorBase} editor The editor widget
     * @preventable
     * @async
     */
    //endregion

    //region Config

    static $name = 'TaskEdit';

    static get pluginConfig() {
        return {
            chain  : ['populateEventMenu', 'onEventEnterKey'],
            assign : ['editEvent']
        };
    }

    static configurable = {
        /**
         * The event that shall trigger showing the editor. Defaults to `eventdblclick`, set to `` or null to
         * disable editing of existing events.
         * @config {String}
         * @default
         * @category Editor
         */
        triggerEvent : 'eventdblclick',

        entityName : 'task',

        /**
         * Project type to editor class map. Editor will be used depending on project, not on product.
         *
         * @config {Object}
         * @internal
         * @category Editor
         */
        editorClassMap : {
            [ProjectType.SchedulerBasic] : 'schedulertaskeditor',
            [ProjectType.SchedulerPro]   : 'schedulertaskeditor',
            [ProjectType.Gantt]          : 'gantttaskeditor'
        },

        // <remove-on-release>
        // TODO: deprecate it in favor of `editorConfig : { type : 'xxx' }`
        // </remove-on-release>
        /**
         * Class to use as the editor. By default it picks editor class depending on the project type.
         * It can be either {@link SchedulerPro.widget.SchedulerTaskEditor} or
         * {@link SchedulerPro.widget.GanttTaskEditor}.
         * By specifying your own `editorClass` you override this.
         * @config {Core.widget.Widget} editorClass
         * @typings {typeof Widget}
         * @category Editor
         */

        /**
         * A configuration object applied to the internal {@link SchedulerPro.widget.TaskEditorBase TaskEditor}.
         * Useful to for example change the title of the editor or to set its dimensions in code:
         *
         * ```javascript
         * features : {
         *     taskEdit : {
         *         editorConfig : {
         *             title : 'My title',
         *             height : 300
         *         }
         *     }
         * }
         * ```
         *
         * NOTE: The easiest approach to affect editor contents is to use the {@link #config-items items config}.
         *
         * @config {TaskEditorBaseConfig} editorConfig
         */

        /**
         * A configuration object used to customize the contents of the task editor. Supply a config object or
         * boolean per tab (listed below) to either affects its contents or toggle it on/off.
         *
         * Supplied config objects will be merged with the tabs predefined configs.
         *
         * To remove existing items, set corresponding keys to `null`.
         *
         * Built-in tab names are:
         *  * `generalTab`
         *  * `predecessorsTab`
         *  * `successorsTab`
         *  * `advancedTab`
         *  * `notesTab`
         *
         * ```
         *  features : {
         *      taskEdit : {
         *          items : {
         *              // Custom settings and additional items for the general tab
         *              generalTab : {
         *                  title : 'Common',
         *                  items : {
         *                      durationField : false,
         *                      myCustomField : {
         *                          type : 'text',
         *                          name : 'color'
         *                      }
         *                  }
         *              },
         *              // Hide the advanced tab
         *              advancedTab : null
         *          }
         *      }
         *  }
         * ```
         *
         *  Please see the `taskeditor` demo for a customized editor in action.
         *  @config {Object} items
         */

        /**
         * True to show a confirmation dialog before deleting the event
         * @config {Boolean}
         * @default
         * @category Editor widgets
         */
        confirmDelete : true,

        /**
         * Set to `false` to not scroll event into view when invoking edit action (e.g. if event is only partially visible)
         * @config {Boolean}
         * @default
         */
        scrollIntoView : true,

        /**
         * How much of a long task bar which is clipped by scrolling must be brought into view
         * to facilitate editing.
         *
         * In a Gantt, or a horizontal Scheduler, this will bring 100 pixels of width into view.
         *
         * In a vertical Scheduler, this will bring 100 pixels of height into view.
         * @prp {Number}
         * @defult
         */
        minEditSize : 100
    };

    //endregion

    //region Constructor/Destructor

    construct(scheduler, config) {
        super.construct(scheduler, ObjectHelper.assign({
            enableEventSpanBuffer : scheduler.features.eventBuffer?.enabled
        }, config));

        scheduler.ion({
            dragCreateEnd  : 'onDragCreateEnd',
            afterEventDrop : 'onEventDropped',
            eventResizeEnd : 'onEventResized',
            thisObj        : this
        });
    }

    doDestroy() {
        this.cleanupProjectListener();

        this.editor?.destroy();

        if (this.deleteConfirmationPromise) {
            MessageDialog.hide();
        }

        super.doDestroy();
    }

    //endregion

    //region Internal

    async normalizeRecord(taskRecord) {
        taskRecord = super.normalizeRecord(...arguments);

        const
            { client }             = this,
            { project, taskStore } = client,
            resourceRecord         = this._resourceRecord;

        // If this is a new record, add it to the store and assign to a resource only after we have started a transaction
        // which can be rolled back in case of Cancel button press
        if (!taskRecord.isOccurrence && !taskStore.includes(taskRecord) && !taskRecord.isCreating) {
            taskRecord.isCreating = true;

            taskStore.add(taskRecord);

            if (resourceRecord) {
                client.assignmentStore.assignEventToResource(taskRecord, resourceRecord);
            }

            await project.commitAsync();
        }

        return taskRecord;
    }

    async editRecordStart({ callArguments, record : taskRecord, targetElement }) {
        const
            { client }     = this,
            { taskStore }  = client,
            resourceRecord = this._resourceRecord;

        // Get the element passed to editEvent() call
        // (targetElement at this stage is already resolved baser on a record)
        const callTargetElement = callArguments[1];

        // For programmatic edit calls for an event not currently in view, scroll it into view first
        if (this.scrollIntoView && !client.timeAxisSubGrid.collapsed && !callTargetElement &&
            taskStore.includes(taskRecord) && (resourceRecord || client.isGantt)
        ) {
            const dimension = client.isVertical ? 'maxHeight' : 'maxWidth';

            await this.scrollEventIntoView(taskRecord, resourceRecord, {
                [dimension] : this.minEditSize,
                animate     : true
            });
        }

        if (!taskRecord.isOccurrence) {
            if (!client.eventStore.includes(taskRecord)) {
                return false;
            }
            else {
                // In case dataset was replaced in listeners above
                taskRecord = client.eventStore.getById(taskRecord.id);
            }
        }

        client.element.classList.add('b-taskeditor-editing');

        return taskRecord;
    }

    afterSave() {
        this.client.element.classList.remove('b-taskeditor-editing');
    }

    afterCancel() {
        super.afterCancel();

        this.client.element.classList.remove('b-taskeditor-editing');
    }

    afterDelete() {
        this.client.element.classList.remove('b-taskeditor-editing');
    }

    getElementFromTaskRecord(taskRecord, resourceRecord) {
        return this.client.getElementFromEventRecord(taskRecord, resourceRecord);
    }

    scrollEventIntoView(eventRecord, resourceRecord, options) {
        this.client.scrollResourceEventIntoView(resourceRecord, eventRecord, options);
    }

    //endregion

    //region Project

    setupProjectListener() {
        super.setupProjectListener();

        this.project.ion({
            name      : 'project',
            dataReady : 'onDataReady',
            thisObj   : this
        });
    }

    //endregion

    onDataReady() {
        let { record } = this;

        // Record could've been removed from project
        if (record) {
            // If project / eventStore was reloaded, fetch record again first
            record = this.scheduler.taskStore.getById(record.id);

            if (record) {
                this.load(record, true);
            }
        }

        if (!record) {
            this.editor.close();
        }
    }

    //region Editor

    onActivateEditor({ eventRecord, resourceRecord, eventElement }) {
        // attempt to re-open already opened editor for the same record - do nothing
        if (eventRecord === this.record && this.isEditing) {
            return;
        }

        this.editEvent(eventRecord, resourceRecord, eventElement);
    }

    getRecordTargetElement(taskRecord) {
        return DomHelper.down(
            this.getElementFromTaskRecord(taskRecord, this._resourceRecord),
            this.client.eventInnerSelector
        );
    }

    /**
     * Shows a {@link SchedulerPro.widget.SchedulerTaskEditor scheduler task editor} or {@link SchedulerPro.widget.GanttTaskEditor gantt task editor}
     * to edit the passed task. This function is exposed on the Scheduler Pro instance and can be called as `scheduler.editTask()`.
     * @param {SchedulerPro.model.EventModel|Function} taskRecord Task to edit or a function returning a task to edit,
     * the function will be executed within an STM transaction which will be canceled in case user presses Cancel button
     * or closes editor w/o hitting Save.
     * @param {SchedulerPro.model.ResourceModel} [resourceRecord] The Resource record for the event. This parameter is
     * required if the event is newly created for a resource and has not been assigned, or when using multi assignment.
     * @param {HTMLElement} [element] Element to anchor editor to (defaults to events element)
     * @returns {Promise} Promise which resolves after the editor is shown
     */
    async editEvent(taskRecord, resourceRecord = null, element = null, stmCapture = null) {
        taskRecord = taskRecord.isEventSegment ? taskRecord.event : taskRecord;

        if (!this.client.isGanttBase && !resourceRecord) {
            // In case of assignments, take the first resource
            resourceRecord = taskRecord.resource || taskRecord.resources?.[0];
        }

        this._resourceRecord = resourceRecord;

        return this.editRecord(taskRecord, element, stmCapture);
    }

    getEditorTypeByRecord(taskRecord) {
        // Editor will be used depending on project, not on product
        const project = taskRecord?.project || this.project;

        return this.editorClassMap[project.getType()];
    }

    prepareEditorConfig(config) {
        const
            me = this,
            { client } = me;

        ObjectHelper.merge(config, {
            eventEditFeature         : me,
            weekStartDay             : me.weekStartDay,
            enableEventSpanBuffer    : me.enableEventSpanBuffer,
            dependencyIdField        : me.editorConfig?.dependencyIdField || client.dependencyIdField,
            durationDisplayPrecision : client.durationDisplayPrecision,
            internalListeners        : {
                delete : 'onDelete'
            }
        });

        if (!client.showTaskColorPickers && config.tabPanelItems?.generalTab !== false) {
            config.tabPanelItems = ObjectHelper.merge(config.tabPanelItems || {}, {
                generalTab : { items : { colorField : false } }
            });
        }

        return super.prepareEditorConfig(config);
    }

    /**
     * The editor widget used for editing task details.
     * Provides an interface to modify task properties within the Scheduler.
     *
     * @category Editor
     * @member {SchedulerPro.widget.SchedulerTaskEditor} editor
     */

    getEditor(taskRecord = this.record, cfg) {
        const editor = super.getEditor(...arguments);

        // Recurrent events are opened read-only, unless this is a reload after choosing to edit it
        editor.readOnly = editor.readOnly || ((taskRecord?.isOccurrence || taskRecord?.isRecurring) && !editor.editingRecurring);

        return editor;
    }

    //endregion

    //region Actions

    load(taskRecord, ...args) {
        const
            { client, editor } = this,
            { widgetMap }      = editor,
            isNestedEvent      = taskRecord.parent && !taskRecord.parent.isRoot;

        // task editor is not meant to edit a segment model so load its main task instead
        if (taskRecord.isEventSegment) {
            taskRecord = taskRecord.event;
        }

        const result = super.load(taskRecord, ...args);

        if (widgetMap.deleteButton) {
            widgetMap.deleteButton.hidden = client.readOnly || taskRecord.isCreating || editor.readOnly;
        }

        if (client.features.nestedEvents) {
            // Editing a nested event, disable parts of the editor
            if (widgetMap.predecessorsTab) {
                widgetMap.predecessorsTab.disabled = isNestedEvent;
            }

            if (widgetMap.successorsTab) {
                widgetMap.successorsTab.disabled = isNestedEvent;
            }

            if (widgetMap.resourcesField) {
                widgetMap.resourcesField.disabled = isNestedEvent;
            }
        }

        return result;
    }

    doSave() {
        const { editor } = this;
        // Reset exceptionDate tracking, to not react to it on the following cancel
        editor.resetRecurrenceData = editor.editingRecurring = null;

        return super.doSave();
    }

    /**
     * Call this method to close task editor reverting changes.
     * @returns {Promise} A promise which is resolved when the task editor is closed and changes are reverted.
     */
    async cancel() {
        const
            me                      = this,
            { editor }              = me,
            { resetRecurrenceData } = editor;

        // Reset exceptionDate on cancel, not handled by STM
        if (resetRecurrenceData) {
            resetRecurrenceData.recurringTimeSpan.exceptionDates = resetRecurrenceData.originalExceptionDates;
            editor.resetRecurrenceData = null;
        }

        return super.cancel();
    }

    //endregion

    //region Events

    async onDelete() {
        const me = this;

        if (me.confirmDelete) {
            // <remove-on-release>
            // TODO: Ask nige about a better solution to prevent popup from closing when showing dialog
            // </remove-on-release>
            const
                { editor } = me,
                autoClose  = editor.autoClose;

            editor.autoClose = false;

            me.deleteConfirmationPromise = MessageDialog.confirm({
                title       : 'L{TaskEdit.ConfirmDeletionTitle}',
                message     : 'L{TaskEdit.ConfirmDeletionMessage}',
                okButton    : 'L{TaskEditorBase.Delete}',
                rootElement : me.rootElement
            });

            const result     = await me.deleteConfirmationPromise;

            editor.autoClose = autoClose;

            me.deleteConfirmationPromise = null;

            if (result === MessageDialog.yesButton) {
                me.delete();
            }
        }
        else {
            // There's might be propagation requested, so we giving the chance to start propagating
            // before we're doing cancel rejection procedure.
            me.requestAnimationFrame(() => me.delete());
        }
    }

    onDragCreateEnd({ eventRecord, resourceRecord, proxyElement, stmCapture }) {
        // Only edit if it a real create. If it is a drag to schedule an already existing
        // event in gantt, then we do not offer the edit UI.
        if (!this.isDestroyed && !this.disabled && eventRecord.isCreating) {
            this.editEvent(eventRecord, resourceRecord, null, stmCapture);
        }
    }

    //endregion

    //region Context menu

    populateEventMenu({ eventRecord, resourceRecord, items }) {
        const canEdit = !eventRecord.readOnly && !this.client.readOnly;

        items.editEvent = {
            text        : 'L{editEvent}',
            localeClass : this,
            icon        : 'b-icon b-icon-edit',
            weight      : -200,
            disabled    : this.disabled,
            hidden      : !canEdit,
            onItem      : () => this.editEvent(eventRecord, resourceRecord)
        };
        items.showDetails = {
            text        : 'L{Object.showDetails}',
            localeClass : this,
            icon        : 'b-icon b-icon-show-details',
            weight      : -200,
            hidden      : canEdit,
            onItem      : () => this.editEvent(eventRecord, resourceRecord)
        };
    }

    // chained from EventNavigation
    onEventEnterKey({ assignmentRecord, eventRecord }) {
        if (assignmentRecord) {
            this.editEvent(eventRecord, assignmentRecord.resource);
        }
        else if (eventRecord) {
            this.editEvent(eventRecord, eventRecord.resource);
        }
    }

    //endregion

    // Fire 2 events with param / event name using 'task' + 'event'
    async triggerOnClient(name, params) {
        params.taskEdit = this;
        params.resourceRecord = this._resourceRecord;

        const returnValTaskRecord = await super.triggerOnClient(name, params);

        params.eventRecord = params.taskRecord;
        // RecurringEvents mixin expects there to be 'eventRecords' in the beforeEventDelete event
        params.eventRecords = [params.taskRecord];

        const returnValEventRecord = await super.triggerOnClient(name, params, 'event');

        return returnValTaskRecord && returnValEventRecord;
    }

    onEventDropped({ eventRecords }) {
        const eventRecord = this.editor?.loadedRecord && eventRecords.find(rec => rec === this.editor.loadedRecord);

        eventRecord && this.onEventResized({ eventRecord });
    }

    onEventResized({ eventRecord }) {
        const { editor } = this;
        if (editor?.isVisible && eventRecord === editor.loadedRecord) {
            editor.realign();
        }
    }
}

GridFeatureManager.registerFeature(TaskEdit, true, 'SchedulerPro');
GridFeatureManager.registerFeature(TaskEdit, false, 'ResourceHistogram');
